import LWElement from './../../lib/lw-element.js';
import ast from './ast.js';
import env from '../../env.js';
import tables from './dashboard-tables.js';
import account from './dashboard-account.js';
import billing from './dashboard-billing.js';
import tickets from './dashboard-tickets.js';
import attachments from '../../lib/ticket-attachments.js';
import admin from './dashboard-admin.js';

// Canonical fragment per page id: the breadcrumb path (sidebar group / page),
// so the URL reads exactly like the menu. Ticket views are sided the same way:
// support/tickets/<id> and system/tickets/<id>.
const ROUTE_FOR_PAGE = {
  'profile': 'account/profile',
  'passkeys': 'account/passkeys',
  'preferences': 'account/preferences',
  'billing': 'billing/balance',
  'my-orders': 'billing/my-orders',
  'my-returns': 'billing/my-returns',
  'my-licenses': 'billing/my-licenses',
  'transactions': 'billing/transactions',
  'tickets': 'support/tickets',
  'products': 'store/products',
  'orders': 'store/orders',
  'returns': 'store/returns',
  'reloads': 'store/reloads',
  'subscriptions': 'store/subscriptions',
  'admin-transactions': 'store/transactions',
  'licenses': 'store/licenses',
  'license-definitions': 'store/license-definitions',
  'promo-codes': 'store/promo-codes',
  'admin-tickets': 'system/tickets',
  'users': 'system/users',
  'security-log': 'system/security-log',
};

// Accepts canonical routes and the pre-hierarchy flat fragments (bookmarks,
// email links), which were the page ids themselves.
const PAGE_FOR_ROUTE = {};
for (const [page, route] of Object.entries(ROUTE_FOR_PAGE)) {
  PAGE_FOR_ROUTE[route] = page;
  PAGE_FOR_ROUTE[page] = page;
}

// Resolves a hash route (fragment before ?params) to a page id, plus the
// ticket id and viewing side for ticket routes. The legacy neutral form
// ticket-view/<id> resolves with no side — the caller falls back to the
// ?src= param / session / server.
function resolveRoute(route) {
  const sided = /^(support|system)\/tickets\/(.+)$/.exec(route);
  if (sided) {
    return { page: 'ticket-view', ticketID: sided[2], src: sided[1] === 'system' ? 'admin' : 'user' };
  }
  const legacy = /^ticket-view\/(.+)$/.exec(route);
  if (legacy) {
    return { page: 'ticket-view', ticketID: legacy[1], src: '' };
  }
  return { page: PAGE_FOR_ROUTE[route] || route };
}

class WebDashboard extends LWElement {  // LWElement extends HTMLElement
    constructor() {
      super(ast);
    }

    // Page id behind the hash route (without ?params or a ticket id);
    // applyRoute() reconciles this precisely once a session is up.
    page = resolveRoute(location.hash.slice(1).split('?')[0]).page || 'profile';
    email = '';
    userName = '';
    userDisplayName = '';
    credentials = [];
    credentialsLoaded = false;
    // Own preferences (account page); rows are {name, value} with value as
    // display-ready JSON text.
    preferences = [];
    preferencesLoaded = false;
    prefForm = { name: '', value: '' };
    prefEditMode = false;
    // false = the shared dialog edits the signed-in account; true = an admin
    // editing userPrefsUser through the users-page dialog (the row click on
    // the users table is the user selection — no picker in the dialog).
    prefAdminMode = false;
    prefSaving = false;
    userPrefsUser = { id: '', email: '' };
    userPrefs = [];
    userPrefsLoaded = false;
    isAdmin = false;
    // Root: may enter the sub-systems (factory, mailadmin) to bootstrap
    // their staff lists, and is the only role that can grant/revoke root.
    isRoot = false;
    smsLogin = false;
    // A test deployment says so before sign-in (GET /api/pub/features) and
    // asks for an acknowledgement after it — see maybeShowTestEnv.
    testEnv = false;
    testEnvHost = '';
    phone = '';
    phoneVerified = '';
    phoneOptedOut = false;
    phoneInput = '';
    phoneConsent = false;
    phoneCodeSent = false;
    phoneCode = '';
    phoneLoading = false;
    phoneVerifyLoading = false;
    users = [];
    usersLoaded = false;
    securityEvents = [];
    securityEventsLoaded = false;
    balance = 0;
    transactions = [];
    transactionsLoaded = false;
    userOrders = [];
    userOrdersLoaded = false;
    returnOrderID = '';
    returnItems = [];
    returnRefundTo = 'gateway';
    returnLoading = false;
    myReturns = [];
    myReturnsLoaded = false;
    myLicenses = [];
    myLicensesLoaded = false;
    viewedLicenseKey = '';
    renewalLicense = {};
    licenseRenewLoading = false;
    licenseBalanceLoading = false;
    licenseBalanceLoaded = false;
    licenseRenewBalanceAmount = 0;
    licenseRenewGateway = '';
    // The promo code applied to the renewal in the dialog, as the store priced it.
    licenseRenewPromo = null;
    licenseRenewPromoCode = '';
    licenseRenewPromoNote = '';
    licenseRenewPromoRefused = false;
    licenseRenewPromoBusy = false;
    licenseRenewMethods = [];
    licenseRenewMethodsLoaded = false;
    licenseRenewMethodsLoading = false;
    licenseRenewals = [];
    licenseRenewalActionID = '';
    licenseDevicesLicense = { devices: [] };
    licenseDevicesSource = 'user';
    verifyHistoryKey = '';
    verifyHistory = [];
    verifyHistoryLicenseID = '';
    verifyHistoryLoaded = false;
    reloadAmount = '';
    reloadMethod = 'stripe';
    // Stripe and PayPal have always been offered unconditionally; gateways
    // added later (Square) appear only when the server reports them
    // configured (loadPaymentMethods), so no dead option is ever shown.
    reloadMethods = ['stripe', 'paypal'];
    reloadLoading = false;
    subscribeLoading = false;
    subscriptionPlans = [];
    subGateway = '';
    planChoice = '';
    subscriptions = [];
    subsTab = 'active';
    products = [];
    productsLoaded = false;
    productForm = { id: '', name: '', description: '', license_product: '', is_active: true };
    productEditMode = false;
    productDialogTitle = '';
    licenseDefinitions = [];
    licenseDefinitionsLoaded = false;
    licenseDefinitionOptions = [];
    licenseDefinitionOptionsLoading = false;
    licenseProductOptions = [];
    licenseProductOptionsLoading = false;
    licenseDefinitionForm = {};
    licenseDefinitionSaving = false;
    creatingProduct = false;
    promoCodes = [];
    promoCodesLoaded = false;
    promoCodeForm = {};
    promoCodeSaving = false;
    licenses = [];
    licensesLoaded = false;
    // Every product that has keys, for the Licenses page's product filter.
    licenseProducts = [];
    licenseForm = { count: 1, product: 'ampere', email: '', name: '', notes: '', start_date: '', end_date: '', renew_period: 0, renew_unit: 'day', renew_choice: 'disabled', renew_price: 0 };
    licenseEditMode = false;
    licenseGenLoading = false;
    orders = [];
    ordersLoaded = false;
    orderDetailItems = [];
    orderDetailTitle = '';
    orderDetailOrder = null;
    orderDetailSource = '';
    returnRequests = [];
    returnRequestsLoaded = false;
    adminReloads = [];
    adminReloadsLoaded = false;
    adminSubscriptions = [];
    adminSubscriptionsLoaded = false;
    adminTransactions = [];
    adminTransactionsLoaded = false;
    refundLicenseTx = {};
    refundLicenseTo = 'gateway';
    refundLicenseLoading = false;
    refundReloadTx = {};
    refundAmount = '';
    refundLoading = false;
    tickets = [];
    ticketsLoaded = false;
    ticketViewSource = '';
    // starValues feeds the lw-for star widgets (list Rating column).
    starValues = [1, 2, 3, 4, 5];
    // Files dragged over the new-ticket dialog ('new' kind; the reply
    // compose lives in web-ticket-view).
    dragTarget = '';
    newTicketSubject = '';
    newTicketBody = '';
    newTicketLoading = false;
    newTicketFiles = [];
    adminTickets = [];
    adminTicketsLoaded = false;
    ticketUnreadCount = 0;
    adminTicketUnreadCount = 0;
    userForm = { id: '', email: '', name: '', display_name: '', is_active: false, is_admin: false, is_root: false };
    userPasskeysUser = { id: '', email: '' };
    userPasskeys = [];
    userPasskeysLoaded = false;
    logoutLoading = false;
    profileLoading = false;
    registerLoading = false;
    confirmTitle = '';
    confirmMessage = '';
    confirmAction = '';
    confirmDanger = false;
    confirmChoices = [];
    confirmChoice = '';
    _confirmResolve = null;
    menuOpen = false;
    themeMode = ['light', 'dark'].includes(localStorage.getItem('theme')) ? localStorage.getItem('theme') : 'auto';

    toggleMenu() {
      this.menuOpen = !this.menuOpen;
    }

    closeMenu() {
      this.menuOpen = false;
    }

    cycleTheme() {
      const order = ['auto', 'light', 'dark'];
      this.themeMode = order[(order.indexOf(this.themeMode) + 1) % order.length];
      if (this.themeMode === 'auto') {
        localStorage.removeItem('theme');
      } else {
        localStorage.setItem('theme', this.themeMode);
      }
      window.__applyTheme?.();
    }

    themeIcon() {
      return { light: 'icon-sun', dark: 'icon-moon' }[this.themeMode] || 'icon-monitor';
    }

    themeTitle() {
      const current = { light: 'light', dark: 'dark' }[this.themeMode] || 'system';
      return 'Switch theme (currently: ' + current + ')';
    }

    avatarInitial() {
      return (this.userDisplayName || this.userName || this.email || '?').trim().charAt(0).toUpperCase();
    }

    // Sidebar footer label: display name, else profile name, else email. New
    // accounts carry the email as a placeholder in both name fields, so a
    // name equal to the email doesn't count.
    sidebarUserLabel() {
      const email = (this.email || '').toLowerCase();
      const display = (this.userDisplayName || '').trim();
      if (display && display.toLowerCase() !== email) return display;
      const name = (this.userName || '').trim();
      if (name && name.toLowerCase() !== email) return name;
      return this.email;
    }

    // --- First-login welcome ---
    // New accounts start with the email as the name placeholder; that (or a
    // missing passkey) triggers a one-time-per-session setup prompt.

    welcomeStep = 0; // 0 = closed, 1 = profile, 2 = passkey
    welcomeLoading = false;

    pageTitle() {
      var titles = {
        'profile': 'Profile',
        'passkeys': 'Passkeys',
        'preferences': 'Preferences',
        'billing': 'Balance',
        'my-orders': 'My Orders',
        'my-returns': 'My Returns',
        'my-licenses': 'My Licenses',
        'transactions': 'Transactions',
        'tickets': 'Tickets',
        'ticket-view': this._ticketView?.ticketDetail?.subject || 'Ticket',
        'products': 'Products',
        'orders': 'Orders',
        'returns': 'Returns',
        'reloads': 'Reloads',
        'subscriptions': 'Subscriptions',
        'admin-transactions': 'Transactions',
        'licenses': 'Licenses',
        'license-definitions': 'License Definitions',
        'promo-codes': 'Promo Codes',
        'admin-tickets': 'Tickets',
        'users': 'Users',
        'security-log': 'Security log',
      };
      return titles[this.page] || this.page;
    }

    pageCategory() {
      // A ticket opened from the admin list belongs to System, not Support;
      // ticketViewSource records which list the viewer came in through.
      if (this.page === 'ticket-view') {
        return this.ticketViewSource === 'admin' ? 'System' : 'Support';
      }
      var categories = {
        'profile': 'Account', 'passkeys': 'Account', 'preferences': 'Account',
        'billing': 'Billing', 'my-orders': 'Billing', 'my-returns': 'Billing', 'my-licenses': 'Billing', 'transactions': 'Billing',
        'tickets': 'Support',
        'products': 'Store', 'orders': 'Store', 'returns': 'Store', 'reloads': 'Store', 'subscriptions': 'Store', 'admin-transactions': 'Store', 'licenses': 'Store', 'license-definitions': 'Store', 'promo-codes': 'Store',
        'admin-tickets': 'System', 'users': 'System', 'security-log': 'System',
      };
      return categories[this.page] || '';
    }

    // The list behind an open ticket, for the intermediate breadcrumb on the
    // ticket view (System / Tickets / <subject>): the side the viewer came in
    // through decides which list "Tickets" points back to.
    ticketListRoute() {
      return this.ticketViewSource === 'admin' ? ROUTE_FOR_PAGE['admin-tickets'] : ROUTE_FOR_PAGE['tickets'];
    }

    async apiFetch(url, options) {
      options = options || {};
      options.headers = options.headers || {};
      const sid = sessionStorage.getItem('session');
      if (sid) {
        options.headers['Authorization'] = 'Bearer ' + sid;
      }
      const response = await fetch(url, options);
      // 401 from a session-protected endpoint means the session is gone;
      // /pub/ endpoints can return 401 for other reasons, so leave those alone.
      if (response.status === 401 && sid && !url.includes('/pub/')) {
        // Tear down only once: parallel requests that carried the same
        // token shouldn't each re-dispatch logout.
        if (sessionStorage.getItem('session') === sid) {
          sessionStorage.removeItem('session');
          sessionStorage.removeItem('testEnvAck'); // asked again on the next sign-in
          this.dispatchEvent(new CustomEvent('logout', { bubbles: true, composed: true, detail: { expired: true } }));
        }
        const err = new Error('Session expired');
        err.sessionExpired = true;
        throw err;
      }
      return response;
    }

    apiUrl(path) {
      return env.apiUrl + path;
    }

    // --- Routing -------------------------------------------------------------
    // The URL hash is the single source of truth. Navigation changes the hash
    // (a history entry, so Back works); applyRoute() — via leanweb's
    // urlHashChanged hook — reads the hash and syncs page + table state. Table
    // pages carry their paging/search/sort as `?`-params in the fragment so a
    // refresh restores exactly the view (e.g. #users?page=2&q=foo).

    _navReady = false;

    // Page name -> backing table name (differ for a few pages).
    _pageTable = {
      users: 'users', orders: 'orders', returns: 'returnRequests', reloads: 'adminReloads',
      subscriptions: 'adminSubscriptions', 'admin-transactions': 'adminTransactions',
      'license-definitions': 'licenseDefinitions', 'promo-codes': 'promoCodes', licenses: 'licenses', 'admin-tickets': 'adminTickets', 'my-orders': 'userOrders',
      'my-returns': 'myReturns', 'my-licenses': 'myLicenses', transactions: 'transactions',
      tickets: 'tickets', products: 'products', passkeys: 'credentials',
      preferences: 'preferences', 'security-log': 'securityEvents',
    };

    // Called by nav links (which carry the real href); just closes the mobile
    // menu — the href drives the route via hashchange.
    navigate(page) {
      this.menuOpen = false;
    }

    // Programmatic navigation: point the hash at `fragment` (route + optional
    // ?params). Setting location.hash pushes a history entry and fires
    // hashchange -> applyRoute.
    _go(fragment) {
      this.menuOpen = false;
      if (location.hash.slice(1) === fragment) {
        this.applyRoute();
      } else {
        location.hash = fragment;
      }
    }

    urlHashChanged() {
      this.applyRoute();
    }

    applyRoute() {
      const hash = location.hash.slice(1);
      const qi = hash.indexOf('?');
      const route = (qi >= 0 ? hash.slice(0, qi) : hash) || 'account/profile';
      const params = new URLSearchParams(qi >= 0 ? hash.slice(qi + 1) : '');
      this.menuOpen = false;

      const r = resolveRoute(route);
      if (r.page === 'ticket-view') {
        const id = r.ticketID;
        // Deep link / refresh straight to a ticket: the owning list gets
        // seeded behind it once the load resolves which side is viewing
        // (_syncTicketRoute), so Back returns to the right list.
        this._seedTicketList = !this._navReady;
        this._navReady = true;
        // Perspective: the sided path, else a legacy URL's src param, else
        // the in-session source, else empty — the server then decides (an
        // admin's own ticket resolves to the customer side, anyone else's
        // to support). _syncTicketRoute rewrites legacy forms once known.
        const src = r.src || params.get('src') || this.ticketViewSource || '';
        // Re-read when the ticket changes — and when the SIDE does, even for
        // the ticket already on screen. An admin reads their OWN ticket from
        // either list and the two have distinct URLs, so a link to the other
        // one has to switch the view rather than leave this one sitting there.
        // (A sided URL is rewritten to the side the server settled on, so this
        // asks the server once, not on a loop.)
        const sameTicket = this.page === 'ticket-view' && this._ticketView?.ticketDetail?.id === id;
        if (!sameTicket || (r.src && r.src !== this.ticketViewSource)) {
          this._ticketView?.viewTicket(id, src);
        }
        return;
      }
      this._navReady = true;

      // Legacy flat fragments canonicalize in place, so the address bar
      // always ends up showing the breadcrumb path.
      const canonical = ROUTE_FOR_PAGE[r.page];
      if (canonical && route !== canonical) {
        history.replaceState(history.state, '', '#' + canonical + (qi >= 0 ? hash.slice(qi) : ''));
      }

      const table = this._pageTable[r.page];
      if (table) this._applyTableParams(table, params);
      this.page = r.page;
      const loading = this.loadPageData();
      this.update();
      this._syncSearchInput();
      if (loading && loading.then) loading.then(() => { this.update(); this._syncSearchInput(); });
    }

    // Read paging/search/sort out of the URL params into a table's state.
    _applyTableParams(name, params) {
      const st = this.tableState(name);
      const page = parseInt(params.get('page'), 10);
      const size = parseInt(params.get('size'), 10);
      st.page = page > 0 ? page : 1;
      st.size = this.pageSizes.includes(size) ? size : this.rememberedPageSize(name);
      st.q = params.get('q') || '';
      st.key = params.get('sort') || '';
      st.dir = params.get('dir') === 'desc' ? -1 : 1;
      // Only when present: absent means "use this table's default", which
      // the loader applies (Support shows all, System hides closed).
      if (params.has('hc')) st.hideClosed = params.get('hc') === '1';
      if (params.has('role')) {
        st.role = ['all', 'admin', 'user'].includes(params.get('role')) ? params.get('role') : 'all';
      }
      if (params.has('status')) {
        st.status = ['unrevoked', 'all', 'active', 'available', 'revoked'].includes(params.get('status'))
          ? params.get('status') : 'unrevoked';
      }
      // A product is spelled the way keys are issued (lower case); empty
      // means every product.
      if (params.has('product')) st.product = (params.get('product') || '').trim().toLowerCase().slice(0, 50);
      if (params.has('type')) {
        st.type = ['all', 'reload', 'subscription', 'payment', 'refund'].includes(params.get('type'))
          ? params.get('type') : 'all';
      }
    }

    // Serialize a table's state to fragment params, omitting defaults.
    _tableHashParams(name) {
      const st = this.tableState(name);
      const p = new URLSearchParams();
      if (st.page > 1) p.set('page', st.page);
      if (st.size !== this.rememberedPageSize(name)) p.set('size', st.size);
      if (st.q.trim()) p.set('q', st.q.trim());
      if (st.key) { p.set('sort', st.key); p.set('dir', st.dir > 0 ? 'asc' : 'desc'); }
      if (st.hideClosed !== undefined) p.set('hc', st.hideClosed ? '1' : '0');
      if (st.role !== undefined) p.set('role', st.role);
      if (st.status !== undefined) p.set('status', st.status);
      if (st.product) p.set('product', st.product);
      if (st.type !== undefined) p.set('type', st.type);
      return p.toString();
    }

    // Reflect the current table page's state into the URL (no new history
    // entry) so a refresh restores it.
    // Called by web-ticket-view once a load resolves which side is viewing:
    // encodes the perspective into the URL for admins (refresh and shared
    // links keep it) and performs the deferred deep-link list seeding.
    _syncTicketRoute(ticketID) {
      if (!/^#(ticket-view\/|(support|system)\/tickets\/)/.test(location.hash)) return;
      const side = this.ticketViewSource === 'admin' ? 'system' : 'support';
      const fragment = side + '/tickets/' + ticketID;
      if (this._seedTicketList) {
        this._seedTicketList = false;
        const list = this.ticketViewSource === 'admin' ? 'admin-tickets' : 'tickets';
        history.replaceState(history.state, '', '#' + ROUTE_FOR_PAGE[list]);
        history.pushState(history.state, '', '#' + fragment);
      } else if (location.hash.slice(1) !== fragment) {
        history.replaceState(history.state, '', '#' + fragment);
      }
    }

    _syncUrl() {
      const name = this._pageTable[this.page];
      if (!name) return;
      const params = this._tableHashParams(name);
      const fragment = (ROUTE_FOR_PAGE[this.page] || this.page) + (params ? '?' + params : '');
      if (location.hash.slice(1) !== fragment) {
        history.replaceState(history.state, '', '#' + fragment);
      }
    }

    // Reflect the restored search term back into the visible search box, and
    // the role filter into its select (form controls keep their own value
    // across re-renders, so route-applied state is pushed in by hand).
    _syncSearchInput() {
      const name = this._pageTable[this.page];
      const input = this.querySelector('.table-search');
      if (input) input.value = name ? this.tableState(name).q : '';
      const role = this.querySelector('.role-filter');
      if (role && name) role.value = this.tableState(name).role || 'all';
      const status = this.querySelector('.status-filter');
      if (status && name) status.value = this.tableState(name).status || 'unrevoked';
      const product = this.querySelector('.product-filter');
      if (product && name) product.value = this.tableState(name).product || '';
      const type = this.querySelector('.type-filter');
      if (type && name) type.value = this.tableState(name).type || 'all';
    }

    // Href for the admin chat-name link: the Users page filtered to that email.
    userProfileHref(email) {
      return '#system/users?q=' + encodeURIComponent(email || '');
    }

    // Loads the data behind the current page. Admin pages are gated on
    // isAdmin so that opening the app on an admin hash as a non-admin
    // doesn't fire admin API calls; a fresh session must therefore await
    // loadUserData() before calling this.
    async loadPageData() {
      if (this.page === 'passkeys') {
        this.loadPasskeyData();
      } else if (this.page === 'preferences') {
        this.loadPreferences();
      } else if (this.page === 'billing') {
        if (this._activatePayPalSub) {
          // One-shot: the activation belongs to the redirect that set it.
          // Awaited so the list below already shows the new subscription.
          const activate = this._activatePayPalSub;
          this._activatePayPalSub = null;
          await activate();
        }
        this.loadSubscriptions();
        this.loadSubscriptionPlans();
        this.loadPaymentMethods();
        await this.loadBalance();
        if (this._watchBalance) {
          // One-shot, armed by a gateway redirect whose credit arrives by
          // webhook: keep re-reading in the background until it lands.
          this._watchBalance = false;
          this.watchBalance();
        }
        if (this._capturePayPal) {
          // One-shot: the capture belongs to the redirect that set it,
          // not to later visits to the billing page.
          const capture = this._capturePayPal;
          this._capturePayPal = null;
          await capture();
        }
      } else if (this.page === 'my-orders') {
        this.loadUserOrders();
      } else if (this.page === 'my-returns') {
        this.loadMyReturns();
      } else if (this.page === 'my-licenses') {
        await Promise.all([this.loadMyLicenses(), this.loadBalance(), this.loadLicenseRenewals()]);
        if (this._licenseRenewalReturn) {
          const result = this._licenseRenewalReturn;
          this._licenseRenewalReturn = null;
          await this.handleLicenseRenewalReturn(result);
        }
        this.watchLicenseRenewals();
      } else if (this.page === 'transactions') {
        this.loadTransactions();
      } else if (this.page === 'tickets') {
        this.loadTickets();
      } else if (this.page === 'admin-tickets' && this.isAdmin) {
        this.loadAdminTickets();
      } else if (this.page === 'products' && this.isAdmin) {
        this.loadProducts();
      } else if (this.page === 'orders' && this.isAdmin) {
        this.loadOrders();
      } else if (this.page === 'returns' && this.isAdmin) {
        this.loadReturnRequests();
      } else if (this.page === 'reloads' && this.isAdmin) {
        this.loadAdminReloads();
      } else if (this.page === 'subscriptions' && this.isAdmin) {
        this.loadAdminSubscriptions();
      } else if (this.page === 'admin-transactions' && this.isAdmin) {
        this.loadAdminTransactions();
      } else if (this.page === 'license-definitions' && this.isAdmin) {
        this.loadLicenseDefinitions();
      } else if (this.page === 'promo-codes' && this.isAdmin) {
        this.loadPromoCodes();
      } else if (this.page === 'licenses' && this.isAdmin) {
        this.loadLicenses();
        this.loadLicenseProducts();
      } else if (this.page === 'users' && this.isAdmin) {
        this.loadUsers();
      } else if (this.page === 'security-log' && this.isAdmin) {
        this.loadSecurityEvents();
      }
    }

    // Session entry point, shared by the two ways a session can start: a
    // fresh page load with a stored session (domReady) and an interactive
    // login (root's onLogin). The dashboard is constructed before login and
    // lw-if only detaches/reattaches it, so domReady alone never sees an
    // interactive login.
    // Clear all account-scoped state. The dashboard instance is reused across
    // logout/login in the same tab (per-tab multi-account is supported), so
    // without this a second account would briefly render the first's tickets,
    // balance, orders and table state until fresh fetches land — and
    // permanently if one of those fetches fails.
    resetSessionState() {
      this.email = '';
      this.userName = '';
      this.userDisplayName = '';
      this.isAdmin = false;
      this.isRoot = false;
      // smsLogin survives: it's deployment config, not account state.
      this.phone = ''; this.phoneVerified = ''; this.phoneOptedOut = false;
      this.phoneInput = ''; this.phoneConsent = false;
      this.phoneCodeSent = false; this.phoneCode = '';
      this.credentials = []; this.credentialsLoaded = false;
      this.preferences = []; this.preferencesLoaded = false;
      this.userPrefs = []; this.userPrefsLoaded = false;
      this.users = []; this.usersLoaded = false;
      this.securityEvents = []; this.securityEventsLoaded = false;
      this.balance = 0;
      this._balanceSeq = (this._balanceSeq || 0) + 1;
      this.subscriptionPlans = []; this.subGateway = ''; this.planChoice = '';
      this.subscriptions = []; this.subsTab = 'active';
      this.transactions = []; this.transactionsLoaded = false;
      this.userOrders = []; this.userOrdersLoaded = false;
      this.myReturns = []; this.myReturnsLoaded = false;
      this.myLicenses = []; this.myLicensesLoaded = false;
      this.renewalLicense = {}; this.licenseRenewLoading = false;
      this.licenseDevicesLicense = { devices: [] }; this.licenseDevicesSource = 'user';
      this._licenseRenewAttempt = null;
      this.licenseBalanceLoading = false; this.licenseBalanceLoaded = false;
      this.licenseRenewBalanceAmount = 0; this.licenseRenewGateway = '';
      this.licenseRenewPromo = null; this.licenseRenewPromoCode = ''; this.licenseRenewPromoNote = ''; this.licenseRenewPromoRefused = false; this.licenseRenewPromoBusy = false;
      this.licenseRenewMethods = []; this.licenseRenewMethodsLoaded = false; this.licenseRenewMethodsLoading = false;
      this.licenseRenewals = []; this.licenseRenewalActionID = '';
      this._licenseRenewalsSeq = (this._licenseRenewalsSeq || 0) + 1;
      this._licenseRenewalRequests = {};
      this._licenseRenewalWatcher = null;
      this.products = []; this.productsLoaded = false;
      this.licenses = []; this.licensesLoaded = false;
      this.licenseProducts = [];
      this._licenseProductsSeq = (this._licenseProductsSeq || 0) + 1;
      this.licenseDefinitions = []; this.licenseDefinitionsLoaded = false;
      this.promoCodes = []; this.promoCodesLoaded = false;
      this.licenseDefinitionOptions = []; this.licenseDefinitionOptionsLoading = false;
      this._licenseDefinitionOptionsSeq = (this._licenseDefinitionOptionsSeq || 0) + 1;
      this.licenseProductOptions = []; this.licenseProductOptionsLoading = false;
      this._licenseProductOptionsSeq = (this._licenseProductOptionsSeq || 0) + 1;
      this.refundLicenseTx = {}; this.refundLicenseLoading = false;
      this.orders = []; this.ordersLoaded = false;
      this.returnRequests = []; this.returnRequestsLoaded = false;
      this.adminReloads = []; this.adminReloadsLoaded = false;
      this.adminSubscriptions = []; this.adminSubscriptionsLoaded = false;
      this.adminTransactions = []; this.adminTransactionsLoaded = false;
      this.tickets = []; this.ticketsLoaded = false;
      this.adminTickets = []; this.adminTicketsLoaded = false;
      this.ticketViewSource = '';
      this.ticketUnreadCount = 0;
      this.adminTicketUnreadCount = 0;
      this._revokePicked(this.newTicketFiles);
      this.newTicketFiles = [];
      // The conversation page's state lives in web-ticket-view; the direct
      // reference works even while that component is parked by its lw-if.
      this._ticketView?.reset();
      this._serverTotals = {};
      this._tableStates = {};
      this._navReady = false;
    }

    async initSession() {
      this.resetSessionState();
      await this.loadUserData();
      this.loadTicketUnreadCounts();
      // Route from the current hash. Must run after loadUserData — isAdmin
      // decides the ticket view and gates admin pages. applyRoute handles
      // normal pages, restored table state, and #ticket-view/<id> deep links.
      this.applyRoute();
    }

    // Tooltip text for a button: its explicit aria-label, else its visible
    // label. Numbered pagination buttons read better as "Go to page N".
    _tooltipFor(btn) {
      const aria = btn.getAttribute('aria-label');
      if (aria) return aria;
      const text = btn.textContent.trim();
      if (!text) return '';
      if (btn.classList.contains('page-btn') && /^\d+$/.test(text)) return 'Go to page ' + text;
      return text;
    }

    // Set a native title on every button under `root` that doesn't already have
    // one. Called for the initial DOM and for each subtree leanweb inserts.
    _applyTooltips(root) {
      if (!root || root.nodeType !== 1) return;
      const buttons = [];
      if (root.matches && root.matches('button')) buttons.push(root);
      if (root.querySelectorAll) root.querySelectorAll('button').forEach(b => buttons.push(b));
      for (const btn of buttons) {
        if (btn.hasAttribute('title')) continue;
        const t = this._tooltipFor(btn);
        if (t) btn.setAttribute('title', t);
      }
    }

    showToast(text, type = 'success', duration = 3000) {
      const container = this.querySelector('.toast-container');
      const toast = document.createElement('div');
      toast.className = 'toast ' + type;

      const icon = document.createElement('i');
      icon.className = 'toast-icon icon ' + (type === 'danger' ? 'icon-alert-circle' : 'icon-check-circle');

      const body = document.createElement('span');
      body.className = 'toast-body';
      body.textContent = text;

      const close = document.createElement('button');
      close.className = 'toast-close';
      close.setAttribute('aria-label', 'Dismiss');
      close.textContent = '×';

      // Toasts must stay readable over open modals: popovers render in the
      // top layer, above <dialog> backdrops (and their blur). Drop back out
      // once the last toast is gone.
      const popoverReady = typeof container.showPopover === 'function';
      const dismiss = () => {
        toast.remove();
        if (popoverReady && !container.childElementCount && container.matches(':popover-open')) {
          container.hidePopover();
        }
      };
      close.onclick = dismiss;

      toast.append(icon, body, close);
      container.appendChild(toast);
      if (popoverReady && !container.matches(':popover-open')) container.showPopover();
      setTimeout(dismiss, duration);
    }

    onResizeStart(e) {
      e.preventDefault();
      const sidebar = this.querySelector('.sidebar');
      const handle = this.querySelector('.resize-handle');
      handle.classList.add('active');
      const onMove = (e) => {
        const width = Math.min(400, Math.max(140, e.clientX));
        sidebar.style.width = width + 'px';
      };
      const onUp = () => {
        handle.classList.remove('active');
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        localStorage.setItem('sidebar-width', sidebar.style.width);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    }

    aaguids = {};

    async domReady() {
      const savedWidth = localStorage.getItem('sidebar-width');
      if (savedWidth) this.querySelector('.sidebar').style.width = savedWidth;

      // Give every button a hover tooltip: its aria-label, else its text label.
      // One pass over the current DOM plus an observer for the buttons leanweb
      // renders later (table rows, pagination, dialogs) covers all of them —
      // and any added in future — without hand-annotating each. Buttons that
      // set their own title (e.g. the theme toggle) are left untouched.
      this._applyTooltips(this);
      new MutationObserver((mutations) => {
        for (const m of mutations) {
          for (const node of m.addedNodes) this._applyTooltips(node);
        }
      }).observe(this, { childList: true, subtree: true });

      // A clipped table cell says the rest of itself in a tooltip, as the
      // factory's grids do (a column is capped at --col-max, web.css). Judged
      // at hover time, so paging, sorting and column changes need no
      // bookkeeping; a cell whose template binds its own title keeps it.
      this.addEventListener('mouseover', (e) => {
        const td = e.target.closest('td');
        if (!td || td.hasAttribute('lw-bind:title')) return;
        if (td.scrollWidth > td.clientWidth) {
          td.title = td.textContent.trim();
          td.dataset.clipTitle = '';
        } else if ('clipTitle' in td.dataset) {
          td.removeAttribute('title');
          delete td.dataset.clipTitle;
        }
      });

      const params = new URLSearchParams(location.search);
      const renewalID = params.get('renewal');
      if (renewalID) {
        this._licenseRenewalReturn = {
          id: renewalID,
          result: params.get('renewal_result') || 'success',
          orderID: params.get('token') || '',
        };
        history.replaceState(null, '', location.pathname + '#billing/my-licenses');
      }
      const subscribeResult = params.get('subscribe');
      if (subscribeResult) {
        // Same contract as ?reload=…: land on the billing page and drop the
        // query. The first credit arrives via webhook, so the balance may
        // trail the toast by a moment.
        history.replaceState(null, '', location.pathname + '#billing/balance');
        if (subscribeResult === 'success') {
          this.showToast('Subscription active! Each recurring reload is credited to your balance automatically.');
          this._watchBalance = true;
        } else if (subscribeResult === 'paypal_approved') {
          // PayPal's approval return: tell the server to record the
          // subscription (the mirror of the one-time paypal_capture).
          // One-shot, run by loadPageData before the subscriptions load.
          const paypalSubID = params.get('subscription_id');
          if (paypalSubID) {
            this._activatePayPalSub = async () => {
              try {
                const response = await this.apiFetch(`${this.apiUrl('billing/paypal-subscription/activate')}`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ subscription_id: paypalSubID }),
                });
                const msg = await response.json();
                if (response.ok) {
                  this.showToast('Subscription active! Each recurring reload is credited to your balance automatically.');
                  this._watchBalance = true;
                } else {
                  this.showToast(msg, 'danger');
                }
              } catch (e) {
                this.showToast(e.message, 'danger');
              }
            };
          }
        }
      }
      const reloadResult = params.get('reload');
      if (reloadResult) {
        // Land on the billing page (applyRoute picks it up from the hash) and
        // drop the ?reload=... query.
        history.replaceState(null, '', location.pathname + '#billing/balance');
        if (reloadResult === 'success') {
          this.showToast('Reload successful! Your balance has been updated.');
          // Credited by the checkout.session.completed webhook, which the
          // redirect back here can beat.
          this._watchBalance = true;
        } else if (reloadResult === 'paypal_capture') {
          const paypalOrderID = params.get('token');
          if (paypalOrderID) {
            this._capturePayPal = async () => {
              try {
                const response = await this.apiFetch(`${this.apiUrl('billing/paypal-capture')}`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ order_id: paypalOrderID }),
                });
                const data = await response.json();
                if (response.ok) {
                  this.balance = data.balance;
                  this.showToast(data.message);
                } else {
                  this.showToast(data, 'danger');
                }
              } catch (e) {
                this.showToast(e.message, 'danger');
              }
            };
          }
        }
      }

      if (!sessionStorage.getItem('session')) return;
      await this.initSession();
    }

    // choices renders radio options in the dialog; the picked value is left
    // in confirmChoice for the caller to read after the await resolves true.
    showConfirm({ title = 'Confirm', message = '', action = 'Confirm', danger = false, choices = [], choice = '' } = {}) {
      this.confirmTitle = title;
      this.confirmMessage = message;
      this.confirmAction = action;
      this.confirmDanger = danger;
      this.confirmChoices = choices;
      this.confirmChoice = choice;
      this.update();
      const dialog = this.querySelector('.confirm-dialog');
      dialog.showModal();
      return new Promise(resolve => {
        this._confirmResolve = resolve;
        // Escape closes the native dialog without going through onConfirm;
        // treat that as a cancel so the caller's await doesn't hang.
        dialog.addEventListener('close', () => {
          if (this._confirmResolve) {
            this._confirmResolve(false);
            this._confirmResolve = null;
          }
        }, { once: true });
      });
    }

    onConfirm(result) {
      // Resolve before close(): closing fires the dialog's 'close' listener,
      // which would otherwise resolve this confirm as cancelled.
      if (this._confirmResolve) {
        this._confirmResolve(result);
        this._confirmResolve = null;
      }
      const dialog = this.querySelector('.confirm-dialog');
      dialog.close();
      document.activeElement?.blur();
      this.querySelector(':focus')?.blur();
    }

    formatAmount(n) {
      if (n == null) return '';
      const num = Number(n);
      // Payments are USD-only (see billing.go), so a fixed $ is accurate.
      return (num < 0 ? '-$' : '$') + Math.abs(num).toFixed(2);
    }

    // Dates and times are written the way the factory writes them (its `en`
    // locale, sites/factory/js/i18n.js LOCALES and ui.js fmtDate/fmtDateTime;
    // user 2026-09-21): MM/DD/YYYY, and for a moment in time
    // MM/DD/YYYY HH:mm:ss in the viewer's own time zone, with seconds, 24-hour.
    // Never the browser's locale style, which wrote "Sep 21, 2026, 3:44 PM"
    // here and DD/MM elsewhere.
    formatDateTime(value) {
      if (!value) return '';
      const s = String(value);
      const d = new Date(s);
      if (isNaN(d)) return s;
      return `${this.formatDate(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
    }

    // A date-only value — a license's start or end (midnight UTC), a date
    // the API gives without a time — is calendar arithmetic on its own day,
    // so no time zone can move it: a zoned timestamp is read on the UTC
    // calendar, a bare date exactly as written. A Date object (from
    // formatDateTime, already in local time) is written by its own fields.
    formatDate(value) {
      if (!value) return '';
      if (value instanceof Date) return `${pad2(value.getMonth() + 1)}/${pad2(value.getDate())}/${value.getFullYear()}`;
      const s = String(value);
      if (/(Z|[+-]\d{2}:?\d{2})$/.test(s)) {
        const d = new Date(s);
        if (!isNaN(d)) return `${pad2(d.getUTCMonth() + 1)}/${pad2(d.getUTCDate())}/${d.getUTCFullYear()}`;
      }
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      return m ? `${m[2]}/${m[3]}/${m[1]}` : s;
    }

    formatStatus(s) {
      if (!s) return '';
      const label = String(s).replace(/_/g, ' ');
      return label.charAt(0).toUpperCase() + label.slice(1);
    }

    // Display label for a payment gateway / method value ('stripe', 'paypal',
    // 'balance'), shared by every Gateway column.
    formatGateway(g) {
      const labels = { stripe: 'Stripe', paypal: 'PayPal', square: 'Square', balance: 'Balance', promo: 'Promo code' };
      return labels[g] || this.formatStatus(g);
    }

    // The badge beside the status of a key the app issued free: DBClient's
    // trial of a month, Ampere's free license that never ends (azcode
    // license_trial.go, where the words are the offer's).
    trialBadge(lic) {
      return lic.product === 'ampere' ? 'Free' : 'Trial';
    }

    trialBadgeTitle(lic) {
      return lic.product === 'ampere'
        ? 'A free license key issued from the app: one per email address and per Mac, never expires, nothing to renew'
        : 'A free trial key issued from the app: one per email address and per device, never renewed';
    }

    statusVariant(s) {
      const variants = {
        'completed': 'success',
        'partially_refunded': 'warning',
        'refunded': 'secondary',
        'refunding': 'warning',
        'credited': 'success',
        'canceled': 'secondary',
        'failed': 'danger',
        'pending': 'warning',
        'approved': 'success',
        'denied': 'danger',
        'cancelled': 'secondary',
        'open': 'success',
        'closed': 'secondary',
        'available': 'secondary',
        'active': 'success',
        'past_due': 'warning',
        'unpaid': 'danger',
        'suspended': 'warning',
        'expired': 'secondary',
        'revoked': 'danger',
        'valid': 'success',
        'invalid': 'danger',
      };
      return variants[s] || 'secondary';
    }

    // Security log Event column: failures read as "<event> failed" in red;
    // successes color by how sensitive the event is.
    securityEventLabel(ev) {
      const label = this.formatStatus(ev.event);
      return ev.outcome === 'failure' ? label + ' failed' : label;
    }

    securityEventVariant(ev) {
      if (ev.outcome === 'failure') return 'danger';
      const variants = { 'login': 'success', 'passkey_created': 'success', 'passkey_deleted': 'warning', 'phone_added': 'success', 'phone_removed': 'warning', 'sms_opt_in': 'success', 'sms_opt_out': 'warning' };
      return variants[ev.event] || 'secondary';
    }

    // Security log Device column: friendly "browser on OS" when recognizable,
    // otherwise the raw user agent — scripted attempts (curl, python) are
    // exactly what an admin wants to see verbatim.
    securityDevice(ev) {
      if (!ev.user_agent) return '—';
      const parsed = this.parseUserAgent(ev.user_agent);
      return parsed === 'Unknown on Unknown' ? ev.user_agent : parsed;
    }

    // --- Generic table sort/search/pagination ---
    // Each list table is keyed by the name of its backing array property
    // (e.g. 'users', 'transactions'). State is created lazily per table.
    // Tables in _serverTables are sorted, searched and paged by the database,
    // so the client only ever holds one page. The passkeys table stays
    // client-side: its list is tiny and its displayed columns are derived in
    // the browser (aaguid names, parsed user agents), so filtering the full
    // local list still covers everything the database has.

    // Rows per page, as the factory's grids offer them (sites/factory/js/grid.js
    // and its list engine's cap): 10 to 500, 50 unless the table's owner picked
    // another and it was remembered (rememberedPageSize).
    pageSizes = [10, 25, 50, 100, 200, 500];
    defaultPageSize = 50;
    _tableStates = {};
    _serverTotals = {};
    _searchTimers = {};

    // Server-paginated tables, mapped to the loader that refetches them.
    _serverTables = {
      users: 'loadUsers',
      securityEvents: 'loadSecurityEvents',
      orders: 'loadOrders',
      returnRequests: 'loadReturnRequests',
      adminReloads: 'loadAdminReloads',
      adminSubscriptions: 'loadAdminSubscriptions',
      adminTransactions: 'loadAdminTransactions',
      licenses: 'loadLicenses',
      verifyHistory: 'loadVerifyHistory',
      licenseDefinitions: 'loadLicenseDefinitions',
      promoCodes: 'loadPromoCodes',
      adminTickets: 'loadAdminTickets',
      userOrders: 'loadUserOrders',
      myReturns: 'loadMyReturns',
      myLicenses: 'loadMyLicenses',
      transactions: 'loadTransactions',
      tickets: 'loadTickets',
      products: 'loadProducts',
    };

    // Columns whose displayed value is derived from the row rather than a
    // plain field, keyed by '<tableName>.<columnKey>'.
    _derivedCells = {
      'credentials.authenticator': c => this.aaguidName(c.aaguid),
      'credentials.label': c => this.parseUserAgent(c.label),
      'credentials.rp_id': c => this.credRP(c),
    };

    // Columns client-mode tables match a search against (displayed values).
    _clientSearchKeys = {
      credentials: ['authenticator', 'label', 'rp_id', 'created'],
      preferences: ['name', 'value'],
    };

    // --- Ticket attachments --------------------------------------------------

    formatFileSize(bytes) {
      if (bytes == null) return '';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }

    async logout() {
      this.logoutLoading = true;
      this.update();
      try {
        const response = await this.apiFetch(`${env.apiUrl}logout`, {
          method: 'POST',
        });

        const msg = await response.json();
        if (!response.ok) {
          this.showToast(msg, 'danger');
          return;
        }
      } catch (error) {
        // An expired session is still a completed logout — fall through.
        if (!error.sessionExpired) {
          this.showToast(error.message, 'danger');
          return;
        }
      } finally {
        this.logoutLoading = false;
      }
      sessionStorage.removeItem('session');
      sessionStorage.removeItem('testEnvAck'); // asked again on the next sign-in
      this.dispatchEvent(new CustomEvent('logout', { bubbles: true, composed: true }));
    }
}

// The page/concern methods live in sibling modules; they run with `this`
// bound to the component instance, exactly as if declared in the class.
Object.assign(WebDashboard.prototype, tables, account, billing, tickets, attachments, admin);

const pad2 = (n) => String(n).padStart(2, '0');

customElements.define('web-dashboard', WebDashboard);
